import { WorkflowState, WorkflowAction, Stage, Message, PrioritizationAssessment } from '../types/workflow.types';

/**
 * Generates a unique session ID for tracking IRIS conversations
 * Format: intake_[timestamp]_[random_string]
 * This ensures each chat session has a unique identifier for the backend
 */
const generateSessionId = (): string => {
  return `intake_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Creates the initial state for the workflow
 * This replaces the multiple useState calls in the original component
 * @param userEmail - The authenticated user's email for tracking submissions
 */
export const createInitialWorkflowState = (userEmail: string): WorkflowState => ({
  // Conversation
  messages: [
    {
      id: crypto.randomUUID(),
      role: "system",
      content: `Welcome ${userEmail || 'User'}!`,
      timestamp: new Date()
    },
    {
      id: crypto.randomUUID(),
      role: "bot",
      content: "Hey! 👋 What can I help you with today? Describe the problem you're trying to solve and I'll help you submit a request.",
      timestamp: new Date()
    }
  ],
  stage: Stage.Intake,
  loading: false,
  
  // Data collection
  intakeData: {
    submitted_by: userEmail || "User",
    date_submitted: new Date().toISOString().split("T")[0]
  },
  finalJSON: null,
  
  // Session
  sessionId: generateSessionId(),
  selectedRequestType: null,
  
  // Results
  asanaTaskUrl: null,
  similarRequests: null,
  
  // Error handling
  submissionError: null,
  
  // UI state
  sidebarOpen: false,
  similarRequestsLoading: false,
  
  // Edit mode tracking (human-in-the-loop)
  isEditMode: false,
  editModeStartTime: null,
  showManualSummaryOption: false,
  lastCompletionAttempt: null,
  
  // Prioritization assessment (pre-submit)
  prioritizationResult: null,
  prioritizationLoading: false,
  prioritizationDismissed: false,
});

/**
 * Main reducer function that handles all state updates
 * This replaces the scattered setState calls throughout the original component
 * Each action type represents a specific state change with predictable behavior
 */
export const workflowReducer = (
  state: WorkflowState, 
  action: WorkflowAction
): WorkflowState => {
  switch (action.type) {
    // MESSAGE MANAGEMENT ACTIONS
    // These three actions handle adding different types of messages to the conversation
    
    case 'ADD_USER_MESSAGE': {
      // Create a new user message with unique ID and timestamp
      const message: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: action.payload,
        timestamp: new Date()
      };
      return {
        ...state,
        messages: [...state.messages, message] // Immutably add to messages array
      };
    }
    
    case 'ADD_BOT_MESSAGE': {
      // Create a new bot response message
      const message: Message = {
        id: crypto.randomUUID(),
        role: 'bot',
        content: action.payload,
        timestamp: new Date()
      };
      return {
        ...state,
        messages: [...state.messages, message]
      };
    }
    
    case 'ADD_SYSTEM_MESSAGE': {
      // Create a new system message (notifications, status updates, etc.)
      const message: Message = {
        id: crypto.randomUUID(),
        role: 'system',
        content: action.payload,
        timestamp: new Date()
      };
      return {
        ...state,
        messages: [...state.messages, message]
      };
    }
    
    // WORKFLOW CONTROL ACTIONS
    // These actions manage the overall flow and state of the intake process
    
    case 'SET_STAGE': {
      // Update the current workflow stage (RequestType -> Intake -> Summarization -> Complete)
      return {
        ...state,
        stage: action.payload
      };
    }
    
    case 'SET_LOADING': {
      // Control loading state for UI feedback during async operations
      return {
        ...state,
        loading: action.payload
      };
    }
    
    case 'UPDATE_INTAKE_DATA': {
      // Merge new intake data with existing data (extracted from conversation)
      // This replaces the updateIntakeData function from the original component
      return {
        ...state,
        intakeData: {
          ...state.intakeData,
          ...action.payload // Spread operator merges new fields with existing ones
        }
      };
    }
    
    case 'SET_FINAL_JSON': {
      // Store the final structured JSON from IRIS processing
      // This contains the classified and organized request data
      return {
        ...state,
        finalJSON: action.payload
      };
    }
    
    case 'SET_REQUEST_TYPE': {
      // Handle request type selection (OP vs Urgent)
      // This automatically sets the target year and request type based on selection
      const currentYear = new Date().getFullYear();
      const targetYear = action.payload === 'op' ? currentYear + 1 : currentYear;
      const requestType = action.payload === 'op' ? 'OP' : 'In-Year';
      
      return {
        ...state,
        selectedRequestType: action.payload,
        stage: Stage.Intake, // Move to intake stage after selection
        intakeData: {
          ...state.intakeData,
          target_year: targetYear.toString(),
          request_type: requestType
        }
      };
    }
    
    case 'COMPLETE_SUBMISSION': {
      // Mark the submission as complete and store the Asana task URL
      return {
        ...state,
        stage: Stage.Complete,
        asanaTaskUrl: action.payload.taskUrl,
        loading: false, // Clear loading state
        submissionError: null // Clear any previous errors
      };
    }
    
    case 'SUBMISSION_ERROR': {
      // Handle submission errors with retry capability
      return {
        ...state,
        loading: false, // Clear loading state
        submissionError: {
          message: action.payload.error,
          timestamp: new Date(),
          retryable: action.payload.retryable,
          context: 'asana_submission'
        }
      };
    }
    
    // SIMILAR REQUESTS ACTIONS
    // These manage the sidebar functionality for showing related requests
    
    case 'SET_SIMILAR_REQUESTS': {
      // Store the similar requests data fetched from the backend
      return {
        ...state,
        similarRequests: action.payload
      };
    }
    
    case 'SET_SIMILAR_REQUESTS_LOADING': {
      // Control loading state specifically for similar requests fetch
      return {
        ...state,
        similarRequestsLoading: action.payload
      };
    }
    
    // UI STATE ACTIONS
    // These manage the user interface state
    
    case 'TOGGLE_SIDEBAR': {
      // Toggle the similar requests sidebar open/closed
      return {
        ...state,
        sidebarOpen: !state.sidebarOpen
      };
    }
    
    case 'SET_SIDEBAR_OPEN': {
      // Explicitly set sidebar open/closed state
      return {
        ...state,
        sidebarOpen: action.payload
      };
    }
    
    case 'RESET': {
      // Reset the entire workflow to initial state (for "New Chat" functionality)
      // This replaces the resetChat function from the original component
      return createInitialWorkflowState(action.payload.userEmail);
    }
    
    // EDIT MODE ACTIONS (Human-in-the-Loop)
    // These manage the enhanced edit mode functionality
    
    case 'ENTER_EDIT_MODE': {
      // Enter edit mode when user declines during summarization
      return {
        ...state,
        isEditMode: true,
        editModeStartTime: new Date(),
        showManualSummaryOption: false // Reset manual option when entering edit mode
      };
    }
    
    case 'EXIT_EDIT_MODE': {
      // Exit edit mode when returning to summarization
      return {
        ...state,
        isEditMode: false,
        editModeStartTime: null,
        showManualSummaryOption: false
      };
    }
    
    case 'SHOW_MANUAL_SUMMARY_OPTION': {
      // Show/hide the manual "Generate Summary" option
      return {
        ...state,
        showManualSummaryOption: action.payload
      };
    }
    
    case 'SET_LAST_COMPLETION_ATTEMPT': {
      // Track when we last attempted completion detection
      return {
        ...state,
        lastCompletionAttempt: new Date()
      };
    }
    
    // PRIORITIZATION ASSESSMENT ACTIONS
    // These manage the pre-submit priority check during intake
    
    case 'SET_PRIORITIZATION_RESULT': {
      // Store the AI prioritization assessment result
      return {
        ...state,
        prioritizationResult: action.payload,
        prioritizationLoading: false
      };
    }
    
    case 'SET_PRIORITIZATION_LOADING': {
      // Control loading state for prioritization check
      return {
        ...state,
        prioritizationLoading: action.payload
      };
    }
    
    case 'DISMISS_PRIORITIZATION': {
      // User dismissed the prioritization card
      return {
        ...state,
        prioritizationDismissed: true
      };
    }
    
    default:
      // Return current state unchanged for unknown actions
      return state;
  }
};
